import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Circle, X } from 'lucide-react';
import {
  Button,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  Field,
  FieldContent,
  FieldIssueFrame,
  FieldLabel,
  InfoHint,
  Input,
  invalidControlAria,
  StepDetailBody,
  StepDetailPanel,
  Textarea,
  useDebouncedFieldUpdate,
  useFieldIssueFrames,
  type BpmnEventDefinition
} from '@bliks-io/ui';
import { DraftIssueSummary } from './DraftIssueSummary';
import { useNodeFieldIssues } from './useNodeFieldIssues';

export interface EventDetailPanelValue {
  eventType?: string;
  name?: string;
  description?: string;
}

export type EventDetailPanelPatch = Partial<EventDetailPanelValue>;

interface Props {
  nodeId: string;
  i18nPrefix: string;
  fieldIdPrefix: string;
  eventTypes: BpmnEventDefinition[];
  value: EventDetailPanelValue;
  onCommit: (patch: EventDetailPanelPatch) => void;
  onClose: () => void;
  /** Optional tooltip next to the panel title (e.g. what a start event means). */
  titleHint?: ReactNode;
  /** Optional tooltip next to the event-type ("Auslöser") field. */
  eventTypeHint?: ReactNode;
  descriptionRequired?: boolean;
}

export function EventDetailPanel({
  nodeId,
  i18nPrefix,
  fieldIdPrefix,
  eventTypes,
  value,
  onCommit,
  onClose,
  titleHint,
  eventTypeHint,
  descriptionRequired
}: Props) {
  const { t } = useTranslation();
  const frameFor = useFieldIssueFrames<'name' | 'description'>(useNodeFieldIssues(nodeId));
  const nameFrame = frameFor('name');
  const descriptionFrame = frameFor('description');

  // Debounced fields fire on every keystroke, so the panel short-circuits
  // here rather than forcing every upstream mutation to dedupe.
  const commit = useCallback(
    (patch: EventDetailPanelPatch) => {
      const unchanged = (Object.entries(patch) as [keyof EventDetailPanelValue, unknown][]).every(
        ([key, next]) => value[key] === next
      );
      if (unchanged) return;
      onCommit(patch);
    },
    [value, onCommit]
  );

  const nameField = useDebouncedFieldUpdate({
    value: value.name ?? '',
    onCommit: (next) => commit({ name: next })
  });

  const descriptionField = useDebouncedFieldUpdate({
    value: value.description ?? '',
    onCommit: (next) => commit({ description: next })
  });

  const selectedEvent = eventTypes.find((evt) => evt.id === value.eventType);

  const handleEventTypeChange = useCallback(
    (next: string | string[] | undefined) => {
      if (typeof next === 'string' && next) {
        commit({ eventType: next });
      }
    },
    [commit]
  );

  const typeFieldId = `${fieldIdPrefix}-type`;
  const nameFieldId = `${fieldIdPrefix}-name`;
  const descriptionFieldId = `${fieldIdPrefix}-description`;

  return (
    <StepDetailPanel>
      <div className="px-4 py-3 border-b bg-surface-subtle border-line">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <Circle className="h-4 w-4 shrink-0 text-fg-secondary" strokeWidth={2} />
            <h3 className="text-sm font-semibold text-foreground truncate">
              {t(`${i18nPrefix}.title`)}
            </h3>
            {titleHint && <InfoHint content={titleHint} side="bottom" />}
          </div>
          <Button
            variant="ghost"
            size="small"
            onClick={onClose}
            className="p-1! h-6! w-6! shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <StepDetailBody>
        <DraftIssueSummary nodeId={nodeId} />
        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor={typeFieldId} className="flex items-center gap-1.5">
              {t(`${i18nPrefix}.eventTypeLabel`)}
              {eventTypeHint && <InfoHint content={eventTypeHint} />}
            </FieldLabel>
            <FieldContent>
              <Combobox value={value.eventType} onValueChange={handleEventTypeChange}>
                <ComboboxTrigger id={typeFieldId}>
                  {selectedEvent ? (
                    <div className="flex items-center gap-2">
                      <selectedEvent.icon size={16} />
                      <span>{t(selectedEvent.labelKey)}</span>
                    </div>
                  ) : null}
                </ComboboxTrigger>
                <ComboboxContent>
                  <ComboboxList>
                    <ComboboxEmpty>{t('common.noOptionsAvailable')}</ComboboxEmpty>
                    {eventTypes.map((ev) => {
                      const Icon = ev.icon;
                      return (
                        <ComboboxItem key={ev.id} value={ev.id}>
                          <div className="flex items-center gap-2 flex-1">
                            <Icon size={16} />
                            <span>{t(ev.labelKey)}</span>
                          </div>
                        </ComboboxItem>
                      );
                    })}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </FieldContent>
          </Field>

          <FieldIssueFrame {...nameFrame}>
            <Field>
              <FieldLabel htmlFor={nameFieldId}>
                {t(`${i18nPrefix}.nameLabel`)}
                <span className="text-destructive ml-1">*</span>
              </FieldLabel>
              <FieldContent>
                <Input
                  id={nameFieldId}
                  aria-required
                  {...invalidControlAria(nameFrame)}
                  value={nameField.value}
                  onChange={(e) => nameField.onChange(e.target.value)}
                  onBlur={nameField.flush}
                  placeholder={t(`${i18nPrefix}.namePlaceholder`)}
                />
              </FieldContent>
            </Field>
          </FieldIssueFrame>

          <FieldIssueFrame {...descriptionFrame}>
            <Field>
              <FieldLabel htmlFor={descriptionFieldId}>
                {t(`${i18nPrefix}.descriptionLabel`)}
                {descriptionRequired && <span className="text-destructive ml-1">*</span>}
              </FieldLabel>
              <FieldContent>
                <Textarea
                  id={descriptionFieldId}
                  aria-required={descriptionRequired}
                  {...invalidControlAria(descriptionFrame)}
                  value={descriptionField.value}
                  onChange={(e) => descriptionField.onChange(e.target.value)}
                  onBlur={descriptionField.flush}
                  placeholder={t(`${i18nPrefix}.descriptionPlaceholder`)}
                  rows={4}
                />
              </FieldContent>
            </Field>
          </FieldIssueFrame>
        </div>
      </StepDetailBody>
    </StepDetailPanel>
  );
}
